// src/components/shared/MultiSelectDropdown.js
//
// A compact multi-select dropdown: a single trigger that opens a checkbox
// panel. Used for filtering (e.g. lead services on accepted quotes). Selection
// is a controlled array of option values; an empty array means "all".
import React, { useEffect, useRef, useState, useMemo } from 'react';
import { FaChevronDown, FaCheck, FaTimes } from 'react-icons/fa';
import styled from 'styled-components';

const Wrapper = styled.div`
  position: relative;
  flex: 1;
  min-width: 0;
`;

const Trigger = styled.button`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 6px;
  width: 100%;
  padding: 6px 10px;
  border-radius: 4px;
  background: ${p => p.theme.colors.background};
  color: ${p => (p.$active ? p.theme.colors.textPrimary : p.theme.colors.textSecondary)};
  border: 1px solid ${p => (p.$open ? p.theme.colors.primary : p.theme.colors.inputGray)};
  font-size: 0.8rem;
  cursor: pointer;
  white-space: nowrap;

  &:hover { border-color: ${p => p.theme.colors.primary}; }

  .label {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  .chevron {
    flex-shrink: 0;
    font-size: 0.7rem;
    transition: transform 0.15s ease;
    transform: rotate(${p => (p.$open ? '180deg' : '0deg')});
  }
`;

const Count = styled.span`
  flex-shrink: 0;
  min-width: 18px;
  height: 18px;
  padding: 0 5px;
  border-radius: 9px;
  background: ${p => p.theme.colors.primary};
  color: #fff;
  font-size: 0.65rem;
  font-weight: 700;
  display: inline-flex;
  align-items: center;
  justify-content: center;
`;

const Panel = styled.div`
  position: absolute;
  top: calc(100% + 4px);
  left: 0;
  right: 0;
  z-index: 1200;
  background: ${p => p.theme.colors.cardBackground || p.theme.colors.background};
  border: 1px solid ${p => p.theme.colors.inputGray};
  border-radius: 6px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.35);
  max-height: 260px;
  display: flex;
  flex-direction: column;
  overflow: hidden;
`;

const SearchRow = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 10px;
  border-bottom: 1px solid ${p => p.theme.colors.inputGray}55;

  input {
    flex: 1;
    min-width: 0;
    border: none;
    background: transparent;
    color: ${p => p.theme.colors.textPrimary};
    font-size: 0.8rem;
    outline: none;
  }
  svg { color: ${p => p.theme.colors.textSecondary}; font-size: 0.75rem; flex-shrink: 0; }
`;

const OptionsList = styled.div`
  overflow-y: auto;
  padding: 4px;

  &::-webkit-scrollbar { width: 6px; }
  &::-webkit-scrollbar-thumb {
    background: ${p => p.theme.colors.inputGray}66;
    border-radius: 3px;
  }
`;

const Option = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  padding: 7px 8px;
  border: none;
  border-radius: 4px;
  background: ${p => (p.$selected ? p.theme.colors.primary + '1f' : 'transparent')};
  color: ${p => p.theme.colors.textPrimary};
  font-size: 0.8rem;
  text-align: left;
  cursor: pointer;

  &:hover { background: ${p => p.theme.colors.primary}22; }

  .check {
    flex-shrink: 0;
    width: 16px;
    height: 16px;
    border-radius: 3px;
    border: 1px solid ${p => (p.$selected ? p.theme.colors.primary : p.theme.colors.inputGray)};
    background: ${p => (p.$selected ? p.theme.colors.primary : 'transparent')};
    color: #fff;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    font-size: 0.6rem;
  }
  .name { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
`;

const Footer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 6px 10px;
  border-top: 1px solid ${p => p.theme.colors.inputGray}55;

  button {
    border: none;
    background: transparent;
    color: ${p => p.theme.colors.primary};
    font-size: 0.75rem;
    font-weight: 600;
    cursor: pointer;
    padding: 2px 4px;
  }
  button:disabled { color: ${p => p.theme.colors.textSecondary}; cursor: default; }
`;

const EmptyHint = styled.div`
  padding: 12px 10px;
  font-size: 0.78rem;
  color: ${p => p.theme.colors.textSecondary};
  text-align: center;
`;

const MultiSelectDropdown = ({
  options = [],
  selected = [],
  onChange,
  placeholder = 'All',
  searchable = true,
}) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const wrapperRef = useRef(null);

  // Close on outside click / Escape
  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) setOpen(false);
    };
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const filtered = useMemo(() => {
    if (!query) return options;
    const q = query.toLowerCase();
    return options.filter(o => o.toLowerCase().includes(q));
  }, [options, query]);

  const toggle = (value) => {
    if (selected.includes(value)) {
      onChange(selected.filter(v => v !== value));
    } else {
      onChange([...selected, value]);
    }
  };

  const summary = selected.length === 0
    ? placeholder
    : selected.length === 1
      ? selected[0]
      : `${selected.length} selected`;

  return (
    <Wrapper ref={wrapperRef}>
      <Trigger
        type="button"
        $open={open}
        $active={selected.length > 0}
        onClick={() => setOpen(o => !o)}
        title={selected.length > 1 ? selected.join(', ') : undefined}
      >
        <span className="label">{summary}</span>
        {selected.length > 1
          ? <Count>{selected.length}</Count>
          : <FaChevronDown className="chevron" />}
      </Trigger>

      {open && (
        <Panel>
          {searchable && options.length > 6 && (
            <SearchRow>
              <FaTimes
                style={{ visibility: query ? 'visible' : 'hidden', cursor: 'pointer' }}
                onClick={() => setQuery('')}
              />
              <input
                autoFocus
                value={query}
                placeholder="Search..."
                onChange={(e) => setQuery(e.target.value)}
              />
            </SearchRow>
          )}

          <OptionsList>
            {filtered.length === 0 ? (
              <EmptyHint>No matches</EmptyHint>
            ) : (
              filtered.map(opt => {
                const isSelected = selected.includes(opt);
                return (
                  <Option
                    key={opt}
                    type="button"
                    $selected={isSelected}
                    onClick={() => toggle(opt)}
                  >
                    <span className="check">{isSelected && <FaCheck />}</span>
                    <span className="name">{opt}</span>
                  </Option>
                );
              })
            )}
          </OptionsList>

          <Footer>
            <button
              type="button"
              onClick={() => onChange([])}
              disabled={selected.length === 0}
            >
              Clear
            </button>
            <button type="button" onClick={() => setOpen(false)}>Done</button>
          </Footer>
        </Panel>
      )}
    </Wrapper>
  );
};

export default MultiSelectDropdown;
